/**
 * Christmas posters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasPostersPack: ToilePack = {
    slug: 'christmas-posters',
    category: 'season',
    name: 'Christmas posters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'tree', label: 'Big tree', subject: 'a tall decorated tree filling the frame, a star at the top and presents at its foot, no text'},
        {key: 'reindeer', label: 'Reindeer', subject: 'a reindeer standing in falling snow with pines behind, seen side on, no text'},
        {key: 'greeting', label: 'Greeting', subject: 'the words "MERRY CHRISTMAS" lettered large in a warm serif with a holly border and nothing else'},
    ],
    styles: [
        {key: 'scandi', label: 'Scandi folk', style: 'scandinavian folk illustration, simplified shapes, no outlines, matte flat colour', palette: 'A limited palette of red, white, forest green and a touch of gold.', framing: 'Centered on a solid cream background, built to read clean at small size.'},
        {key: 'vintage', label: 'Vintage card', style: 'mid-century christmas card illustration, soft halftone grain, slight misregistration', palette: 'Muted red, pine green, cream and a faded gold.', framing: 'Centered on an aged cream paper background with an even margin.'},
        {key: 'cozy', label: 'Cozy cartoon', style: 'cozy cartoon illustration, rounded shapes, thick soft outlines, warm and friendly', palette: 'Warm reds and greens with a glowing amber and snow white.', framing: 'Centered on a solid background with a little room around it.'},
    ],
    directions: ['a snowy cabin instead', 'for the front window', 'in a square format'],
    directionHint: 'try: a snowy cabin instead',
    seo: {
        title: 'Christmas Posters: Tree, Reindeer & Greeting',
        metaDescription: "Nine Christmas posters — a big decorated tree, a reindeer in the snow, and a lettered 'Merry Christmas' greeting — bold, flat, three finishes.",
        h1: 'Christmas posters: tree, reindeer, and greeting',
        tagline: 'Nine posters, three subjects, three finishes each — bold and flat, made to read from across a room.',
        intro: "Three Christmas posters — a tall decorated tree, a reindeer standing in falling snow, and a large lettered 'Merry Christmas' greeting — each rendered bold and flat, in Scandi folk, vintage card, and cozy cartoon finishes. Ask for a snowy cabin instead, a window-sized crop, or a square format, and the same poster grammar carries it.",
        useCases: [
            'a shop window display',
            'a classroom or office wall',
            'a printed holiday wall art piece',
            'a party backdrop poster',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each poster is composed as a single vertical page; scale it up at a print shop for a larger wall or window size.'},
            {q: 'Is this a photograph of a physical print, or an illustration?', a: 'An illustration, styled bold and flat to read from a distance — not a photo of a printed poster.'},
            {q: 'Can I get a snowy cabin instead, or a square format?', a: 'Yes — the direction field takes any variation, including a different scene or format.'},
            {q: 'What are the three finishes?', a: 'Scandi folk (flat simplified shapes), vintage card (soft halftone grain), and cozy cartoon (rounded warm outlines).'},
        ],
    },
}
